import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { backlogIssueUrl, formatPt } from './format';
import type { OutputHygieneItem } from '@/lib/api';

interface WipTableProps {
  items: OutputHygieneItem[];
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 仕掛かり案件の一覧。全件が着手中のため、Backlogの状態ではなく未入力項目と保留かどうかを示す
 * @graph-connects none
 */
export function WipTable({ items }: WipTableProps) {
  return (
    <GlassCard>
      <div className="mb-2 flex items-center gap-2">
        <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          仕掛かり案件
        </p>
        <Badge variant="info" size="sm">
          {items.length}件
        </Badge>
      </div>
      <p className="mb-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
        着手中の案件です（状態が完了のものは残量ではないので除いています）。「保留」は難易度などの
        判断で意図的に止めているもので、更新が無くても放置ではありません。
      </p>
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr>
              {['キー', '案件名', 'Team', 'Pt', '更新なし日数', '未入力'].map((header) => (
                <th
                  key={header}
                  className="sticky top-0 whitespace-nowrap pb-2 pr-3 text-xs font-medium"
                  style={{
                    background: 'var(--color-bg-surface)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.issueKey} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td className="py-2 pr-3 align-top whitespace-nowrap">
                  <a
                    href={backlogIssueUrl(item.issueKey)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-medium underline"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    {item.issueKey}
                  </a>
                </td>
                <td
                  className="py-2 pr-3 align-top text-xs"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <span>{item.summary}</span>
                  {item.onHold && (
                    <span className="ml-2 inline-block align-middle" title={item.holdReason ?? ''}>
                      <Badge variant="default" size="sm">
                        保留
                      </Badge>
                    </span>
                  )}
                </td>
                <td
                  className="py-2 pr-3 align-top text-xs"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {item.team ?? '—'}
                </td>
                <td
                  className="py-2 pr-3 align-top text-xs tabular-nums"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {formatPt(item.pt)}
                </td>
                <td
                  className="py-2 pr-3 align-top text-xs tabular-nums"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {item.daysSinceUpdate ?? '—'}
                </td>
                <td className="py-2 align-top">
                  <div className="flex flex-wrap gap-1">
                    {item.missingFields.map((field) => (
                      <Badge key={field} variant="warning" size="sm">
                        {field}
                      </Badge>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <p className="py-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>
            仕掛かりはありません。
          </p>
        )}
      </div>
    </GlassCard>
  );
}
